import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { toast } from "sonner";
import { usePublicClient } from "wagmi";
import { GATEWAY_ABI } from "@/contract/abi/gateway";
import ContractAddress from "@/contract/addresses";
import { useEffectiveAddress } from "@/hooks/auth/useEffectiveAddress";
import { useGuardedWriteContract } from "@/hooks/auth/useGuardedWrite";
import { useContractErrorHandler } from "@/hooks/useContractErrorHandler";

export const useCalculateWeekFinal = () => {
  const t = useTranslations("stake");
  const { handleContractError } = useContractErrorHandler();
  const address = useEffectiveAddress();
  const { writeContractAsync } = useGuardedWriteContract();
  const queryClient = useQueryClient();
  const pc = usePublicClient();

  return useMutation({
    mutationFn: async () => {
      const userFromContract = await pc?.readContract({
        abi: GATEWAY_ABI,
        functionName: "getUser",
        args: [address as `0x${string}`],
        address: ContractAddress.GATEWAY,
      });

      const lastUserOrderId = Number(userFromContract?.last_order.order_id);
      const lastOrderId = await pc?.readContract({
        abi: GATEWAY_ABI,
        address: ContractAddress.GATEWAY,
        functionName: "order_id",
      });

      for (let i = lastUserOrderId; i <= Number(lastOrderId); i += 35) {
        const res = await writeContractAsync({
          abi: GATEWAY_ABI,
          functionName: "calculateWeek",
          address: ContractAddress.GATEWAY,
        });
        if (res) {
          await pc?.waitForTransactionReceipt({ hash: res });
          queryClient.invalidateQueries({
            queryKey: ["order-behind"],
          });
        } else {
          throw new Error("Transaction failed!");
        }
      }
    },
    onSuccess: () => {
      toast.success(t("toasts.calculateSuccess"));
      queryClient.invalidateQueries({
        queryKey: ["order-behind"],
      });
      // Dropped a stale invalidation of ["contract-user"] here -- that key
      // was never actually registered by any query (useFetchContractUser
      // reads via wagmi's useReadContract, which useCalculateWeek's own
      // onSuccess already refetches directly), so it was a pure no-op.
      // These two ARE real: the calculate-guide page's week-by-week/weeks
      // list views were never invalidated after a real on-chain calculation,
      // so they kept showing stale/empty results until a full page reload.
      queryClient.invalidateQueries({
        queryKey: ["user", "week-by-week-bv"],
      });
      queryClient.invalidateQueries({
        queryKey: ["user", "weeks-list"],
      });
    },
    onError: (e) => {
      handleContractError(e);
      queryClient.invalidateQueries({
        queryKey: ["order-behind"],
      });
    },
  });
};
